import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import OtpVerifyModal from "../otp/OtpVerifyModal";
import { OTP_PURPOSE } from "../../services/otpApi";
import { getMyBank, saveMyBank, verifyBankEmail, formatDate } from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// BANK DETAILS + EMAIL OTP VERIFICATION  (Steps 17, 18, 19 · Part 9)
//
// Step 17 — Add Bank, or Edit Bank if one is already saved. One form for both.
// Step 18 — the email must be verified before the details count.
// Step 19 — only a verified bank receives a payout; an unverified one holds.
//
// ── MIGRATED OFF FIREBASE AUTHENTICATION ──
// This screen used to ask for the account password, sign in to Firebase in the
// browser and send the resulting ID token to the server. Firebase Auth is gone
// from the product, so it now uses the app's own Resend OTP — the SAME system
// registration and forgot-password use, which is what the brief requires
// ("connect bank operations to it", not build a second one):
//
//     Add Bank Details  → Email OTP → Verify OTP → Save
//     Edit Bank Details → Email OTP → Verify OTP → Allow edit → Save
//
// The user experience is better as well as simpler: no password re-entry, and
// the code goes to the account's own address, which is the thing being proven.
//
// ── NOTHING THIS PAGE SENDS CAN CLAIM VERIFICATION ──
// The OTP dialog returns a single-use TICKET, minted by the server only after
// a correct code. The save request carries it, and the server SPENDS it before
// writing anything — so a failed or skipped verification saves nothing at all
// and leaves the previously stored details untouched.
//
// EDITING RESETS VERIFICATION — a verified email says nothing about an account
// number that was changed afterwards. The server enforces that; this page just
// says so out loud before the creator saves.
// =====================================================

const EMPTY_FORM = {
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  ifsc: "",
  phone: "",
};

function ProBank() {
  const navigate = useNavigate();

  const [state, setState] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldError, setFieldError] = useState({});

  // ── OTP CONFIRMATION ──
  // `otpFor` is null when no dialog is open, otherwise "save" or "reverify":
  // both use the same dialog and the same OTP system, and differ only in what
  // they do with the ticket it returns.
  const [otpFor, setOtpFor] = useState(null);

  const flash = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMyBank();
      setState(res);
      setEditing(!res.hasBank);
      if (!res.hasBank) setForm(EMPTY_FORM);
    } catch (err) {
      flash(err?.response?.data?.message || "Failed to load bank details", "error");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldError((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Which OTP the server will require for the NEXT save. The server decides
  // this from the database and echoes it on GET /bank; a ticket minted for
  // "add" cannot authorise a change to details that already exist, so asking
  // for the wrong one would simply be refused.
  const savePurpose = state?.otpPurpose || (state?.hasBank ? OTP_PURPOSE.BANK_EDIT : OTP_PURPOSE.BANK_ADD);

  // ── STEP 1 of the save: ask for the code ──
  //
  //   Add Bank Details  → Email OTP → Verify OTP → Save
  //   Edit Bank Details → Email OTP → Verify OTP → Allow edit → Save
  //
  // Submitting the form does NOT save. It opens the confirmation dialog, which
  // emails a code to the account's own address. Nothing is written until that
  // code has been verified and the resulting ticket spent by the server.
  const save = (e) => {
    e.preventDefault();
    setFieldError({});

    if (!state?.accountEmail) {
      flash("This account has no email address on file.", "error");
      return;
    }

    setOtpFor("save");
  };

  // ── STEP 2 of the save: the code was accepted; spend the ticket ──
  // Called by the dialog with the single-use ticket. The SERVER re-verifies it
  // before it writes anything, so a stale or forged value saves nothing and
  // leaves the previously stored details exactly as they were.
  const saveWithTicket = async (otpTicket) => {
    setOtpFor(null);
    setSaving(true);

    try {
      const res = await saveMyBank({
        accountHolderName: form.accountHolderName.trim(),
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        ifsc: form.ifsc.trim().toUpperCase(),
        phone: form.phone.trim(),
        otpTicket,
      });

      flash(res.message, "success");
      setForm(EMPTY_FORM);
      setEditing(false);
      await load();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.field) setFieldError({ [data.field]: data.message });
      flash(data?.message || "Failed to save bank details", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── RE-VERIFY AN ALREADY-SAVED ACCOUNT (Step 18) ──
  // For the one case the save path does not cover: a record whose verification
  // was cleared later, where the creator must re-prove ownership WITHOUT
  // re-entering their account number. Identical check, identical OTP system.
  const reverifyWithTicket = async (otpTicket) => {
    setOtpFor(null);
    setSaving(true);

    try {
      const res = await verifyBankEmail(otpTicket);
      flash(res.message, "success");
      await load();
    } catch (err) {
      flash(err?.response?.data?.message || "Verification failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  const bank = state?.bank;

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container pro-container-narrow">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🏦 BANK DETAILS</div>
          <h1 className="pro-hero-title">Where your payouts go</h1>
          <p className="pro-hero-sub">
            Only verified bank accounts receive payouts.
          </p>
        </header>

        {/* ── CURRENT DETAILS ── */}
        {bank && !editing && (
          <section className="pro-card pro-bank-card">
            <div className="pro-bank-head">
              <h2>Saved account</h2>
              <span
                className={`pro-status-pill pro-status-${
                  bank.payable ? "active" : "on_hold"
                }`}
              >
                {bank.payable ? "✓ Verified" : "Not verified"}
              </span>
            </div>

            <dl className="pro-kv pro-kv-stack">
              <div>
                <dt>Account holder</dt>
                <dd>{bank.accountHolderName}</dd>
              </div>
              <div>
                <dt>Bank</dt>
                <dd>{bank.bankName}</dd>
              </div>
              <div>
                <dt>Account number</dt>
                <dd>{bank.accountNumberMasked}</dd>
              </div>
              <div>
                <dt>IFSC</dt>
                <dd>{bank.ifsc}</dd>
              </div>
              {bank.phone && (
                <div>
                  <dt>Contact</dt>
                  <dd>{bank.phone}</dd>
                </div>
              )}
              <div>
                <dt>Email verified</dt>
                <dd>
                  {bank.emailVerified
                    ? `Yes — ${formatDate(bank.emailVerifiedAt)}`
                    : "No"}
                </dd>
              </div>
            </dl>

            <div className="pro-form-actions">
              <button
                type="button"
                className="pro-btn pro-btn-ghost"
                onClick={() => {
                  // The saved account number is never sent to the browser, so
                  // an edit is always a re-entry. That is deliberate: it is
                  // also what makes the creator re-confirm where money goes.
                  setForm({
                    accountHolderName: bank.accountHolderName || "",
                    bankName: bank.bankName || "",
                    accountNumber: "",
                    ifsc: bank.ifsc || "",
                    phone: bank.phone || "",
                  });
                  setEditing(true);
                }}
              >
                ✏️ Edit bank
              </button>

              {!bank.emailVerified && (
                <button
                  type="button"
                  className="pro-btn pro-btn-primary"
                  onClick={() => setOtpFor("reverify")}
                  disabled={saving}
                >
                  ✉️ Verify by email code
                </button>
              )}
            </div>
          </section>
        )}

        {/* ── ADD / EDIT FORM (Step 17) ── */}
        {editing && (
          <form className="pro-card pro-form" onSubmit={save}>
            <h2 className="pro-form-title">
              {state.hasBank ? "Edit bank details" : "Add bank details"}
            </h2>

            {state.hasBank && (
              <div className="pro-notice pro-notice-warn pro-notice-inline">
                <p>
                  Changing the account number or IFSC clears your email
                  verification — you will need to verify again before the next
                  payout.
                </p>
              </div>
            )}

            <div className="pro-field">
              <label className="pro-label" htmlFor="pb-holder">Account holder name</label>
              <input
                id="pb-holder"
                className={`pro-input${fieldError.accountHolderName ? " pro-input-error" : ""}`}
                value={form.accountHolderName}
                onChange={set("accountHolderName")}
                maxLength={100}
                placeholder="As printed on your passbook"
                disabled={saving}
                required
              />
              {fieldError.accountHolderName && (
                <p className="pro-error">{fieldError.accountHolderName}</p>
              )}
            </div>

            <div className="pro-field">
              <label className="pro-label" htmlFor="pb-bank">Bank name</label>
              <input
                id="pb-bank"
                className={`pro-input${fieldError.bankName ? " pro-input-error" : ""}`}
                value={form.bankName}
                onChange={set("bankName")}
                maxLength={100}
                placeholder="State Bank of India"
                disabled={saving}
                required
              />
              {fieldError.bankName && <p className="pro-error">{fieldError.bankName}</p>}
            </div>

            <div className="pro-field">
              <label className="pro-label" htmlFor="pb-acc">Account number</label>
              <input
                id="pb-acc"
                className={`pro-input${fieldError.accountNumber ? " pro-input-error" : ""}`}
                value={form.accountNumber}
                onChange={set("accountNumber")}
                inputMode="numeric"
                maxLength={30}
                placeholder="Digits only"
                disabled={saving}
                required
              />
              {fieldError.accountNumber && (
                <p className="pro-error">{fieldError.accountNumber}</p>
              )}
            </div>

            <div className="pro-field">
              <label className="pro-label" htmlFor="pb-ifsc">IFSC code</label>
              <input
                id="pb-ifsc"
                className={`pro-input${fieldError.ifsc ? " pro-input-error" : ""}`}
                value={form.ifsc}
                onChange={set("ifsc")}
                maxLength={20}
                placeholder="SBIN0001234"
                style={{ textTransform: "uppercase" }}
                disabled={saving}
                required
              />
              {fieldError.ifsc && <p className="pro-error">{fieldError.ifsc}</p>}
            </div>

            <div className="pro-field">
              <label className="pro-label" htmlFor="pb-phone">
                Contact number <span className="pro-optional">(optional)</span>
              </label>
              <input
                id="pb-phone"
                className={`pro-input${fieldError.phone ? " pro-input-error" : ""}`}
                value={form.phone}
                onChange={set("phone")}
                maxLength={20}
                placeholder="So we can reach you if a transfer fails"
                disabled={saving}
              />
              {fieldError.phone && <p className="pro-error">{fieldError.phone}</p>}
            </div>

            {/* ── STEP 18 / PART 9 — EMAIL OTP, BEFORE THE SAVE ── */}
            <div className="pro-notice pro-notice-inline">
              <p>
                <strong>Email verification</strong> — when you continue we'll email a
                6-digit code to <strong>{state.accountEmail}</strong>. Nothing is saved
                until you enter it, so{" "}
                {state.hasBank ? "your current details stay as they are" : "nothing is stored"}{" "}
                if you stop here.
              </p>
            </div>

            <p className="pro-fineprint">
              🔒 We never ask for a UPI PIN, a net-banking password or card details, and
              there is nowhere in this system to store one. The 6-digit code is the only
              thing we will ever ask you for by email, and we will never ask you to read
              it out to anyone. Your account number is encrypted before it is saved.
            </p>

            <div className="pro-form-actions">
              {state.hasBank && (
                <button
                  type="button"
                  className="pro-btn pro-btn-ghost"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
              <button type="submit" className="pro-btn pro-btn-primary" disabled={saving}>
                {saving
                  ? "Saving…"
                  : state.hasBank
                  ? "Continue — verify by email"
                  : "Continue — verify by email"}
              </button>
            </div>
          </form>
        )}

        {/* ── THE OTP DIALOG (Part 9) ──
            One dialog, two callers: the save path and the stand-alone
            re-verification. It emails the code, collects it, and hands back a
            single-use ticket — it never performs the action itself. */}
        <OtpVerifyModal
          open={otpFor !== null}
          purpose={otpFor === "reverify" ? OTP_PURPOSE.BANK_EDIT : savePurpose}
          title={
            otpFor === "reverify"
              ? "Verify your email"
              : state?.hasBank
              ? "Confirm this bank change"
              : "Confirm your bank details"
          }
          lead={
            otpFor === "reverify"
              ? "Confirm you still control this account's email address."
              : "This is where your payouts will be sent, so we confirm it by email first."
          }
          confirmLabel={otpFor === "reverify" ? "Verify" : "Verify & save"}
          onCancel={() => setOtpFor(null)}
          onVerified={otpFor === "reverify" ? reverifyWithTicket : saveWithTicket}
        />

        {/* The stand-alone "verify email" form that used to sit here is gone:
            it asked for the account password to re-authenticate with Firebase.
            Re-verification now runs through the SAME OtpVerifyModal above (see
            the "reverify" branch), so there is one verification path on this
            screen rather than two that could drift apart. */}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/pro-earnings")}
        >
          ← Earnings
        </button>
      </div>
    </div>
  );
}

export default ProBank;
